'use client';

import { useFestivalContext } from '@/contexts/FestivalContext';
import { useUserStore } from '@/store/useUserStore';
import { getApiAuthHeaders } from '@/utils/api-helpers';
import { handleFormSubmitApiError } from '@/utils/form-submit-error';
import { cn } from '@/utils/tailwind-merge';
import { pressSchema } from '@/utils/validation-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import AccreditationSuccess from '../blocks/AccreditationSuccess';
import FilterDropdown from '../blocks/FilterDropdown';
import Button from '../ui/Button';
import Input from '../ui/Input';

type FormData = z.infer<typeof pressSchema>;
const URL = process.env.NEXT_PUBLIC_API_URL || 'https://image-nation.org/api-service';

export default function PressForm({ className }: { className?: string }) {
    const t = useTranslations('PressForm');
    const locale = useLocale();
    const { user, session } = useUserStore();
    const tGeneral = useTranslations();
    const [submitting, setSubmitting] = useState(false);
    const [isSuccessful, setIsSuccessful] = useState(false);

    const [selectedOutreach, setSelectedOutreach] = useState<string[]>([]);
    const [selectedSpecification, setSelectedSpecification] = useState<string[]>([]);
    const [selectedFormat, setSelectedFormat] = useState<string[]>([]);

    const { festival } = useFestivalContext();

    const {
        handleSubmit,
        register,
        reset,
        control,
        formState: { errors },
    } = useForm<FormData>({
        resolver: zodResolver(pressSchema),
    });

    const handleOutreachChange = (filterId: string) => {
        setSelectedOutreach((prev) =>
            prev.includes(filterId) ? prev.filter((id) => id !== filterId) : [...prev, filterId],
        );
    };

    const handleSpecificationChange = (filterId: string) => {
        setSelectedSpecification((prev) =>
            prev.includes(filterId) ? prev.filter((id) => id !== filterId) : [...prev, filterId],
        );
    };

    const handleFormatChange = (filterId: string) => {
        setSelectedFormat((prev) =>
            prev.includes(filterId) ? prev.filter((id) => id !== filterId) : [...prev, filterId],
        );
    };

    const onSubmit = async (formData: FormData) => {
        setSubmitting(true);

        if (!user) {
            setSubmitting(false);
            return;
        }
        try {
            const response = await fetch(`${URL}/form/accreditation/press`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept-Language': locale,
                    ...getApiAuthHeaders(session?.access_token),
                },
                body: JSON.stringify({
                    user_id: user.id,
                    formData: {
                        ...formData,
                        outreach: selectedOutreach.join(','),
                        specification: selectedSpecification.join(','),
                        format: selectedFormat.join(','),
                    },
                    festival_id: festival?.id,
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to submit the form');
            }

            reset();
            setIsSuccessful(true);
        } catch (error) {
            handleFormSubmitApiError(error, t);
        } finally {
            setSubmitting(false);
        }
    };

    return isSuccessful ? (
        <div
            className={cn(
                'w-full flex flex-col gap-7 justify-center items-center relative',
                className,
            )}
        >
            <AccreditationSuccess className="mt-20 w-full" isPress />
        </div>
    ) : (
        <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className={`flex flex-col gap-8 text-white ${className}`}
        >
            <div className="flex flex-col gap-4">
                <h4 className="text-white text-[32px] lg:text-[46px] uppercase">
                    {t('informationMedia')}
                </h4>
                <Input
                    {...register('mediaName')}
                    className="newsletter-input"
                    color="white"
                    placeholder={t('mediaName')}
                    error={errors.mediaName?.message}
                />
            </div>

            <div className="flex flex-col gap-4">
                <h3 className="text-2xl uppercase">{t('location')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                        {...register('streetAddress')}
                        className="newsletter-input"
                        color="white"
                        placeholder={t('streetAddress')}
                    />
                    <Input
                        {...register('city')}
                        className="newsletter-input"
                        color="white"
                        placeholder={t('city')}
                    />
                    <Input
                        {...register('province')}
                        className="newsletter-input"
                        color="white"
                        placeholder={t('province')}
                    />
                    <Input
                        {...register('postalCode')}
                        className="newsletter-input"
                        color="white"
                        placeholder={t('postalCode')}
                    />
                    <Input
                        {...register('country')}
                        className="newsletter-input"
                        color="white"
                        placeholder={t('country')}
                    />
                    <Input
                        {...register('email')}
                        className="newsletter-input"
                        color="white"
                        placeholder={t('email')}
                    />
                </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
                <Input
                    {...register('headQuarterAddress')}
                    className="newsletter-input"
                    color="white"
                    placeholder={t('headQuarterAddress')}
                    error={errors.headQuarterAddress?.message}
                />
            </div>

            <div className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FilterDropdown
                        label={t('outreach')}
                        filters={[
                            { id: 'local', label: t('local') },
                            { id: 'national', label: t('national') },
                            { id: 'international', label: t('international') },
                        ]}
                        initialSelectedFilters={selectedOutreach}
                        onFilterChange={handleOutreachChange}
                        onClearAll={() => setSelectedOutreach([])}
                        asInput
                    />
                    <FilterDropdown
                        label={t('format')}
                        filters={[
                            { id: 'print', label: t('print') },
                            { id: 'digital', label: t('digital') },
                        ]}
                        initialSelectedFilters={selectedFormat}
                        onFilterChange={handleFormatChange}
                        onClearAll={() => setSelectedFormat([])}
                        asInput
                    />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FilterDropdown
                        label={t('specification')}
                        filters={[
                            { id: 'web', label: t('web') },
                            { id: 'blog', label: t('blog') },
                            { id: 'podcast', label: t('podcast') },
                            { id: 'tv', label: t('tv') },
                            { id: 'socialmedia', label: t('socialMedia') },
                            { id: 'radio', label: t('radio') },
                            { id: 'other', label: t('other') },
                        ]}
                        initialSelectedFilters={selectedSpecification}
                        onFilterChange={handleSpecificationChange}
                        onClearAll={() => setSelectedSpecification([])}
                        asInput
                    />
                    <Input
                        {...register('website')}
                        className="newsletter-input"
                        color="white"
                        placeholder={t('website')}
                        error={errors.website?.message}
                        type={'url'}
                    />
                </div>
                <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2">
                        <Input
                            {...register('mediaDistribution')}
                            className="newsletter-input"
                            color="white"
                            placeholder={t('mediaDistribution')}
                        />
                        <p className="mt-2.5 font-helvetica-neue font-light text-lg text-white/60">
                            {t('mediaDistributionDescription')}
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex flex-col gap-4">
                <h4 className="text-white text-[32px] lg:text-[46px] uppercase">
                    {t('yourInformation')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <Input
                            {...register('title')}
                            className="newsletter-input"
                            color="white"
                            placeholder={t('title')}
                            error={errors.title?.message}
                        />
                        <p className="mt-2.5 font-helvetica-neue font-light text-lg text-white/60">
                            {t('titleDescription')}
                        </p>
                    </div>
                    <Input
                        {...register('email')}
                        className="newsletter-input"
                        color="white"
                        placeholder={t('emailOfPassholder')}
                        error={errors.email?.message}
                        type="email"
                    />
                </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                    <Input
                        {...register('coveragePlan')}
                        className="newsletter-input"
                        color="white"
                        placeholder={t('coveragePlan')}
                        error={errors.coveragePlan?.message}
                    />
                    <p className="mt-2.5 font-helvetica-neue font-light text-lg text-white/60">
                        {t('coveragePlanDescription')}
                    </p>
                </div>
            </div>

            <Button
                className="w-full md:w-1/4 bg-primary text-black uppercase p-4"
                textClasses="text-black"
                type="submit"
                title={tGeneral('Form.save')}
                submitting={submitting}
                disabled={submitting}
            />
        </form>
    );
}
